<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { request } from '../services/api'
import SharedGameToolbar from '../components/SharedGameToolbar.vue'
import LiuheChat from '../components/LiuheChat.vue'
import LiuheQuickSheet from '../components/LiuheQuickSheet.vue'

const route = useRoute()
const game = ref(null)
const isHongKong = computed(() => game.value?.game_name === 'hklhc')
const historySource = computed(() => isHongKong.value ? 'hklhc' : 'macaujc2')
const state = ref(null)
const types = ref([])
const history = ref([])
const active = ref('')
const loading = ref(false)
const refreshFeedback = ref('')
let refreshController = null
let refreshFeedbackTimer
const error = ref('')
const historyPage = ref(1)
const historyTotal = ref(0)
const lastLottery = ref(null)
const liveDraw = ref(null)
const liveDelayed = ref(false)
let liveTimer, liveController
const liveComplete = computed(() => String(liveDraw.value?.game_no) === String(state.value?.game_no) && liveDraw.value?.numbers?.length === 7)
const drawing = computed(() => state.value?.runtime_ready === true && !currentDrawAvailable.value
  && ['open', 'closed', 'waiting', 'finished'].includes(state.value?.schedule_status)
  && clockNow.value >= Number(state.value?.close_at || Infinity) * 1000)
const liveNumbers = computed(() => String(liveDraw.value?.game_no) === String(state.value?.game_no) ? liveDraw.value.numbers : [])
async function refreshLive() {
  if (disposed || !drawing.value || liveComplete.value || liveController) return
  const period = String(state.value.game_no)
  const controller = new AbortController()
  liveController = controller
  const timeout = window.setTimeout(() => controller.abort(), 8000)
  try {
    const result = await request('/game/history', { method: 'POST', signal: controller.signal,
      data: { game: route.params.game, source: isHongKong.value ? 'hk-live' : 'live2' } })
    if (disposed || String(state.value?.game_no) !== period) return
    const row = result.data
    if (String(row?.game_no) !== period || !Array.isArray(row.numbers) || row.numbers.length > 7
      || row.numbers.some(n => !Number.isInteger(n) || n < 1 || n > 49) || new Set(row.numbers).size !== row.numbers.length) throw new Error('Invalid live draw')
    const previous = liveNumbers.value
    if (row.numbers.length < previous.length || previous.some((n, i) => n !== row.numbers[i])) throw new Error('Regressed live draw')
    liveDraw.value = row
    liveDelayed.value = false
    if (row.numbers.length === 7) void refresh()
  } catch (_) {
    if (!disposed && String(state.value?.game_no) === period) liveDelayed.value = true
  } finally {
    window.clearTimeout(timeout)
    if (liveController === controller) liveController = null
  }
}
watch(() => state.value?.game_no, () => { liveDraw.value = null; liveDelayed.value = false; liveController?.abort() })
const tab = ref('play')
const showHistory = ref(false)
const profile = ref(null)
const bill = ref(null)
const unread = ref(0)
const chat = ref(null)
const accountError = ref('')
const selected = ref([])
const showQuickSheet = ref(false)
const quickSelections = ref({})
const quickAmount = ref('')
const betAmounts = ref({})
const betting = ref(false)
const reviewBet = ref(false)
const betNotice = ref('')
const betSucceeded = ref(false)
const showBetSuccessToast = ref(false)
const receiptPlays = ref([])
let successCloseTimer, successToastTimer
const bettingOpen = computed(() => state.value?.runtime_ready === true && state.value?.schedule_status === 'open'
  && !currentDrawAvailable.value && clockNow.value < Number(state.value?.close_at || 0) * 1000)
const selectedPlays = computed(() => betSucceeded.value ? receiptPlays.value : plays.value.filter(play => selected.value.includes(play.play_id)))
const amountCents = play => /^\d{1,9}(?:\.\d{1,2})?$/.test(String(betAmounts.value[play.play_id] || '')) ? Math.round(Number(betAmounts.value[play.play_id]) * 100) : 0
const totalStake = computed(() => selectedPlays.value.reduce((sum, play) => sum + amountCents(play), 0) / 100)
const potentialProfit = play => Math.max(0, amountCents(play) / 100 * (Number(play.bet_odds) - 1))
const totalProfit = computed(() => selectedPlays.value.reduce((sum, play) => sum + potentialProfit(play), 0))
const canBet = computed(() => bettingOpen.value && !betting.value && !betSucceeded.value && selectedPlays.value.length > 0
  && totalStake.value <= balance.value && selectedPlays.value.every(play => amountCents(play) > 0
    && amountCents(play) / 100 >= Number(play.bet_min || 0)
    && (!Number(play.bet_max_once) || amountCents(play) / 100 <= Number(play.bet_max_once))))
function resetBets() { if (betting.value) return; selected.value = []; betAmounts.value = {}; quickAmount.value = ''; reviewBet.value = false; betNotice.value = '' }
function dismissBet() {
  if (betting.value && !betSucceeded.value) return
  window.clearTimeout(successCloseTimer)
  reviewBet.value = false
  if (betSucceeded.value) { selected.value = []; betAmounts.value = {}; quickAmount.value = ''; betSucceeded.value = false; receiptPlays.value = [] }
}
function applyQuickAmount() { selectedPlays.value.forEach(play => { betAmounts.value[play.play_id] = quickAmount.value }) }
watch(() => state.value?.game_no, () => { if (betSucceeded.value || betting.value) return; selected.value = []; betAmounts.value = {}; quickAmount.value = ''; reviewBet.value = false })
watch(bettingOpen, open => { if (!open && !betSucceeded.value && !betting.value) reviewBet.value = false })
function togglePlay(play) {
  if (!bettingOpen.value || betting.value || betSucceeded.value) return
  selected.value = selected.value.includes(play.play_id) ? selected.value.filter(id => id !== play.play_id) : [...selected.value, play.play_id]
  if (!selected.value.includes(play.play_id)) delete betAmounts.value[play.play_id]
  else betAmounts.value[play.play_id] = quickAmount.value
  if (!selected.value.length) reviewBet.value = false
  betNotice.value = ''
}
const numericPlays = computed(() => plays.value.filter(play => /^\d{2}$/.test(play.play_title) && Number(play.play_title) >= 1 && Number(play.play_title) <= 49))
const selectedNumbers = computed(() => numericPlays.value.filter(play => selected.value.includes(play.play_id)).map(play => Number(play.play_title)))
function selectQuickNumbers({ key, numbers, select }) {
  if (!bettingOpen.value || betting.value || betSucceeded.value) return
  const matches = numericPlays.value.filter(play => numbers.includes(Number(play.play_title)))
  if (!matches.length) return
  const next = { ...quickSelections.value }
  if (select) next[key] = matches.map(play => Number(play.play_title))
  else delete next[key]
  quickSelections.value = next
  const protectedNumbers = new Set(Object.values(next).flat())
  for (const play of matches) {
    if ((!select && !protectedNumbers.has(Number(play.play_title)) && selected.value.includes(play.play_id))
      || (select && !selected.value.includes(play.play_id))) togglePlay(play)
  }
}
watch(() => state.value?.zodiac?.year, (year, oldYear) => {
  if (oldYear != null && year !== oldYear && !betting.value && !betSucceeded.value) resetBets()
})
async function submitBet() {
  if (!canBet.value) return
  betting.value = true; betNotice.value = ''
  const submittedPlays = selectedPlays.value.map(play => ({ ...play }))
  try {
    const response = await request('/game/bet/bet_batch', { method: 'POST', data: {
      game: game.value.game_id, game_no: state.value.game_no,
      bet_lists: selectedPlays.value.map(play => ({ play_id: play.play_id, bet_money: String(betAmounts.value[play.play_id]), bet_content: play.bet_content })),
    } })
    if (disposed) return
    chat.value?.acceptBetReceipt(response.data)
    receiptPlays.value = submittedPlays; betSucceeded.value = true; reviewBet.value = true; showBetSuccessToast.value = true
    window.clearTimeout(successCloseTimer); window.clearTimeout(successToastTimer)
    successCloseTimer = window.setTimeout(dismissBet, 2000)
    successToastTimer = window.setTimeout(() => { showBetSuccessToast.value = false }, 3500)
    void refresh()
  } catch (err) { betNotice.value = err.message || '提交结果未确认，请先查看我的投注，勿重复提交'; reviewBet.value = false }
  finally { betting.value = false }
}
const clockNow = ref(Date.now())
let serverOffset = 0
let clockTimer
let lastBoundary = 0
const currentDrawAvailable = computed(() => lastLottery.value?.source === historySource.value
  && String(lastLottery.value.game_no) === String(state.value?.game_no)
  && numbers(lastLottery.value.history_lottery_numbers).length === 7)
const showFinished = computed(() => (currentDrawAvailable.value || liveComplete.value) && state.value?.schedule_status !== 'disabled')
const unopened = computed(() => state.value?.schedule_status === 'before_open' || showFinished.value)
const closedBoard = computed(() => unopened.value || ['closed', 'waiting', 'finished', 'disabled'].includes(state.value?.schedule_status))
const countdownTarget = computed(() => Number(showFinished.value ? state.value?.next_open_at : state.value?.countdown_at) || 0)
const nextOpenLabel = computed(() => countdownTarget.value ? beijingTime(countdownTarget.value).slice(5, 16) : '')
const displayPeriod = computed(() => {
  if (isHongKong.value) return showFinished.value && state.value?.next_game_no ? state.value.next_game_no : state.value?.game_no || '未开盘'
  if (!showFinished.value || !countdownTarget.value) return state.value?.game_no || '未开盘'
  const date = new Date(countdownTarget.value * 1000 + 8 * 3600000)
  const year = date.getUTCFullYear()
  const day = Math.floor((Date.UTC(year, date.getUTCMonth(), date.getUTCDate()) - Date.UTC(year, 0, 1)) / 86400000) + 1
  return `${year}${String(day).padStart(3, '0')}`
})
const periodLabel = computed(() => drawing.value && !liveComplete.value ? '已封盘'
  : state.value?.schedule_status === 'open' ? '本期已开盘'
  : state.value?.schedule_status === 'before_open' ? '等待开盘' : state.value?.schedule_label || '未设置排期')
const countdown = computed(() => {
  const seconds = Math.max(0, Math.ceil((countdownTarget.value * 1000 - clockNow.value) / 1000))
  if (!countdownTarget.value) return '—'
  return [Math.floor(seconds / 3600), Math.floor(seconds % 3600 / 60), seconds % 60].map(n => String(n).padStart(2, '0')).join(':')
})
const chatEnabled = computed(() => Number(game.value?.game_chat || 0) === 1)
watch(drawing, active => { if (active) void refreshLive() })
const balance = computed(() => Number(profile.value?.balance ?? profile.value?.money ?? 0))
const money = value => value == null ? '—' : Number(value).toFixed(2)
watch(chatEnabled, enabled => { if (!enabled) { tab.value = 'play'; unread.value = 0 } })
let timer
let disposed = false
const currentType = computed(() => types.value.find(type => type.type_name === active.value))
const plays = computed(() => currentType.value?.play || [])
watch(selectedNumbers, numbers => {
  quickSelections.value = Object.fromEntries(Object.entries(quickSelections.value).filter(([, group]) => group.every(number => numbers.includes(number))))
})
const red = new Set([1, 2, 7, 8, 12, 13, 18, 19, 23, 24, 29, 30, 34, 35, 40, 45, 46])
const blue = new Set([3, 4, 9, 10, 14, 15, 20, 25, 26, 31, 36, 37, 41, 42, 47, 48])
const color = number => red.has(Number(number)) ? 'red' : blue.has(Number(number)) ? 'blue' : 'green'
const label = number => String(number).padStart(2, '0')
function numbers(raw) {
  const values = String(raw || '').split(',').map(value => Number(value.trim()))
  return values.length === 7 && new Set(values).size === 7 && values.every(n => Number.isInteger(n) && n >= 1 && n <= 49) ? values : []
}
function historyTimestamp(raw) {
  return /^\d+$/.test(String(raw)) ? Number(raw) : Date.parse(String(raw).replace(' ', 'T') + '+08:00') / 1000
}
function beijingTime(timestamp) {
  return new Date(timestamp * 1000 + 8 * 3600000).toISOString().slice(0, 19).replace('T', ' ')
}
function sourceLabel(row) {
  const timestamp = historyTimestamp(row.history_lottery_time || row.history_open_time)
  return Number.isFinite(timestamp) ? (row.source === 'hklhc' ? beijingTime(timestamp).slice(0, 10) : beijingTime(timestamp)) : ''
}
async function loadHistory(page = historyPage.value, signal) {
  // Source history is display-only, independent of the database settlement cutover.
  const result = await request('/game/history', { method: 'POST', signal, data: {
    game: route.params.game, source: historySource.value, page, pagesize: 10,
  } })
  if (disposed) return
  const data = result.data
  const rows = Array.isArray(data) ? data : data?.lists || []
  if (rows.some(row => row.source !== historySource.value)) {
    history.value = []; lastLottery.value = null; historyTotal.value = 0
    throw new Error('开奖记录接口尚未更新，请上传本次 PHP 文件')
  }
  history.value = rows.filter(row => numbers(row.history_lottery_numbers).length === 7)
  historyTotal.value = Number(result.page?.item_total || data?.total || 0)
  historyPage.value = page
  if (page === 1) lastLottery.value = history.value[0] || null
}
async function refresh(page = historyPage.value, manual = false) {
  if (loading.value) return
  loading.value = true
  error.value = ''
  refreshFeedback.value = ''
  window.clearTimeout(refreshFeedbackTimer)
  const controller = new AbortController()
  refreshController = controller
  const timeout = window.setTimeout(() => controller.abort(), 30000)
  const signal = controller.signal
  try {
    const [list, status, odds] = await Promise.all([
      request('/game/lists', { signal }),
      request('/game/state', { method: 'POST', signal, data: { game: route.params.game } }),
      request('/game/odds', { method: 'POST', signal, data: { game: route.params.game } }),
    ])
    if (disposed) return
    game.value = (list.data?.lists || []).find(row => String(row.game_id) === String(route.params.game))
    if (!game.value) throw new Error('游戏已禁用或您没有访问权限，请返回大厅')
    state.value = status.data
    if (state.value?.server_time) serverOffset = Number(state.value.server_time) * 1000 - Date.now()
    clockNow.value = Date.now() + serverOffset
    // First rollout: TMA only. Other markets remain stored for later activation.
    types.value = Array.isArray(odds.data) ? odds.data.filter(type => type.type_name === 'tma') : []
    if (!types.value.some(type => type.type_name === active.value)) active.value = types.value[0]?.type_name || ''
    try { await loadHistory(page, signal) }
    catch (err) { if (!disposed) error.value = err.message || '开奖记录读取失败，请稍后重试' }
    const account = await Promise.allSettled([
      request('/user/money', { signal }),
      request('/game/bet/today', { method: 'POST', signal, data: { game: route.params.game } }),
    ])
    if (disposed) return
    if (account[0].status === 'fulfilled') profile.value = account[0].value.data
    if (account[1].status === 'fulfilled') bill.value = account[1].value.data
    accountError.value = account.some(item => item.status === 'rejected') ? '账户信息读取失败，请刷新重试' : ''
  } catch (err) { if (!disposed) error.value = err.message || '读取失败，请重试' }
  finally {
    window.clearTimeout(timeout)
    if (!disposed) {
      if (signal.aborted) error.value = '刷新超时，请点击↻重试'
      if (manual) {
        refreshFeedback.value = error.value || accountError.value ? '刷新未完成' : '已刷新'
        refreshFeedbackTimer = window.setTimeout(() => { refreshFeedback.value = '' }, 3000)
      }
    }
    controller.abort()
    refreshController = null
    loading.value = false
  }
}
onMounted(() => {
  liveTimer = window.setInterval(() => { void refreshLive() }, 3000)
  void refresh(); timer = window.setInterval(() => refresh(), 15000)
  clockTimer = window.setInterval(() => {
    clockNow.value = Date.now() + serverOffset
      const boundary = countdownTarget.value
    if (boundary && clockNow.value >= boundary * 1000 && lastBoundary !== boundary && !loading.value) {
      lastBoundary = boundary; void refresh()
    }
  }, 1000)
})
onUnmounted(() => { disposed = true; liveController?.abort(); window.clearInterval(liveTimer); refreshController?.abort(); window.clearTimeout(refreshFeedbackTimer); window.clearInterval(timer); window.clearInterval(clockTimer); window.clearTimeout(successCloseTimer); window.clearTimeout(successToastTimer) })
</script>

<template>
  <main class="liuhe-page">
    <SharedGameToolbar :game-info="game || { game_title: '新澳門六合彩' }" :draw-guard="state?.draw_guard" :user-profile="profile" :available-balance="balance" />
    <nav class="liuhe-tabs" aria-label="游戏页面">
      <button :class="{ active: tab === 'play' }" @click="tab = 'play'">下注<template v-if="countdownTarget"> ({{ countdown }})</template></button>
      <button v-if="chatEnabled" :class="{ active: tab === 'chat' }" @click="tab = 'chat'">聊天室<b v-if="unread">{{ unread > 99 ? '99+' : unread }}</b></button>
    </nav>
    <section class="account-strip">
      <span>可用金额：<b>{{ money(profile?.balance ?? profile?.money) }}</b></span>
      <span>未结金额：<em>{{ money(bill?.bet_money_uncheck) }}</em></span>
      <span>今日输赢：<em>{{ money(bill?.bet_money_pl) }}</em></span>
    </section>
    <p v-if="error || accountError" class="error" role="alert">{{ error || accountError }}</p>
    <section v-show="tab === 'play'" class="play-stage" :class="{ 'board-closed': closedBoard }">
    <div class="liuhe-scroll">
      <section class="last-area">
        <div class="last-title">
          <span class="last-period">{{ drawing ? state.game_no : lastLottery?.game_no || '—' }} 期</span>
          <div v-if="drawing" class="balls live-balls"><template v-if="liveNumbers.length"><template v-for="index in 7" :key="`${state.game_no}:${index}:${liveNumbers[index - 1] || 0}`"><b v-if="index === 7" class="plus">＋</b><i :class="['ball', liveNumbers[index - 1] ? color(liveNumbers[index - 1]) + ' revealed' : 'pending-ball']">{{ liveNumbers[index - 1] ? label(liveNumbers[index - 1]) : '○' }}</i></template></template><span v-else>正在进行搅珠中…</span></div>
          <div v-else class="balls"><template v-for="(n, index) in numbers(lastLottery?.history_lottery_numbers)" :key="index"><b v-if="index === 6" class="plus">＋</b><i :class="['ball', color(n)]">{{ label(n) }}</i></template><span v-if="!lastLottery">暂无开奖记录</span></div>
          <button @click="showHistory = !showHistory">{{ showHistory ? '收起' : '历史' }}</button>
        </div>
        <p v-if="drawing && (liveDelayed || (liveNumbers.length && !liveComplete))" class="live-status" role="status">{{ liveDelayed ? '开奖更新稍有延迟' : '正在进行搅珠中…' }}</p>
      </section>
      <section v-if="showHistory" class="draws">
        <p v-if="!history.length" class="status">暂无{{ isHongKong ? '香港' : '新澳門' }}开奖记录，等待有效开奖结果</p>
        <article v-for="row in history" :key="row.history_id" class="draw">
          <div><strong>{{ row.game_no }} 期</strong><small>{{ sourceLabel(row) }}</small></div>
          <div class="balls"><template v-for="(n, index) in numbers(row.history_lottery_numbers)" :key="index"><b v-if="index === 6" class="plus">＋</b><i :class="['ball', color(n)]">{{ label(n) }}</i></template></div>
        </article>
        <nav class="pages"><button :disabled="loading || historyPage <= 1" @click="refresh(1)">首页</button><button :disabled="loading || historyPage <= 1" @click="refresh(historyPage - 1)">上一页</button><span>第 {{ historyPage }} 页 · 每页10期</span><button :disabled="loading || (historyTotal ? historyPage * 10 >= historyTotal : history.length < 10)" @click="refresh(historyPage + 1)">下一页</button></nav>
      </section>
      <section class="period-bar">
        <span>{{ displayPeriod }} 期</span>
        <span v-if="showFinished || (isHongKong && state?.schedule_status === 'before_open')" class="finished-state">等待开盘<small v-if="nextOpenLabel">开盘 {{ nextOpenLabel }} · {{ countdown }}</small></span>
        <span v-else>{{ periodLabel }}<template v-if="countdownTarget"> · {{ countdown }}</template></span>
        <div class="refresh-control"><small role="status">{{ loading ? '刷新中' : refreshFeedback }}</small><button :disabled="loading" :aria-busy="loading" :aria-label="loading ? '正在刷新游戏' : '刷新游戏'" @click="refresh(historyPage, true)"><span :class="{ spinning: loading }">↻</span></button></div>
      </section>
      <nav v-if="types.length > 1" class="types" aria-label="玩法分类"><button v-for="type in types" :key="type.type_id" :class="{ active: active === type.type_name }" @click="active = type.type_name">{{ type.type_title }}</button></nav>
      <section class="play-section">
        <div v-if="numericPlays.length" class="quick-toolbar"><strong>号码</strong><button type="button" @click="showQuickSheet = true">ϟ 快捷键</button></div>
        <p v-if="!types.length">暂无可用玩法，请联系管理员检查安装及权限</p>
        <div class="plays"><article v-for="play in plays" :key="play.play_id" role="button" :tabindex="bettingOpen ? 0 : -1" :aria-disabled="!bettingOpen || betting" :aria-pressed="selected.includes(play.play_id)" :aria-label="`投注${play.play_title}`" :class="{ selected: selected.includes(play.play_id) }" @click="togglePlay(play)" @keydown.enter.prevent="togglePlay(play)" @keydown.space.prevent="togglePlay(play)"><i v-if="/^\d{2}$/.test(play.play_title)" :class="['ball', color(play.play_title)]">{{ play.play_title }}</i><strong v-else>{{ play.play_title }}</strong><small>{{ Number(play.bet_odds).toFixed(2) }}</small></article></div>
      </section>
    </div>
    <footer v-if="tab === 'play' && (selectedPlays.length || !bettingOpen)" class="bet-action">
      <button :disabled="!bettingOpen || betting" @click="reviewBet = true">{{ bettingOpen ? '立即下注' : '未开放下注' }}</button>
      <button v-if="bettingOpen" class="reset" :disabled="betting" @click="resetBets">重 置</button>
    </footer>
    <div v-if="unopened" class="game-close-state" role="status">未开盘</div>
    </section>
    <p v-if="betNotice" class="bet-feedback" role="status">{{ betNotice }}</p>
    <LiuheQuickSheet v-if="showQuickSheet" :mapping="state?.zodiac?.mapping" :year="state?.zodiac?.year" :selected-numbers="selectedNumbers" :selected-shortcuts="Object.keys(quickSelections)" :disabled="!bettingOpen || betting || betSucceeded" @select="selectQuickNumbers" @close="showQuickSheet = false" />
    <div v-if="showBetSuccessToast" class="bet-success-toast" role="status">投注成功</div>
    <div v-if="reviewBet" class="bet-confirm-mask" @click.self="!betSucceeded && dismissBet()">
      <section class="bet-confirm-dialog" role="dialog" aria-modal="true" aria-label="投注确认">
        <header><span>可用：<b>{{ money(balance) }}</b></span><span>{{ countdown }}</span><button aria-label="关闭" :disabled="betting" @click="dismissBet">×</button></header>
        <div class="confirm-quick"><span>快速金额：</span><input v-model="quickAmount" aria-label="快速金额" placeholder="快速金额" inputmode="decimal" :disabled="betting || betSucceeded" @input="applyQuickAmount" /></div>
        <div class="confirm-head"><b>号码</b><b>赔率</b><b>金额</b><b>可盈</b><b>操作</b></div>
        <div class="confirm-rows"><div v-for="play in selectedPlays" :key="play.play_id" class="confirm-row">
          <span>{{ play.play_title }}</span><span>{{ Number(play.bet_odds).toFixed(2) }}</span>
          <input v-model="betAmounts[play.play_id]" :aria-label="`${play.play_title}下注金额`" inputmode="decimal" :disabled="betting || betSucceeded" />
          <span>{{ money(potentialProfit(play)) }}</span><span v-if="betSucceeded" class="confirm-success-check" aria-label="下注成功">✓</span><button v-else :aria-label="`删除${play.play_title}`" :disabled="betting" @click="togglePlay(play)">×</button>
        </div></div>
        <div class="confirm-summary"><span>投注数：<b>{{ selectedPlays.length }}</b></span><span>总金额：<b>{{ money(totalStake) }}</b></span><span>可盈利：<b>{{ money(totalProfit) }}</b></span></div>
        <button class="confirm-submit" :class="{ success: betSucceeded }" :disabled="!betSucceeded && !canBet" @click="betSucceeded ? dismissBet() : submitBet()">{{ betSucceeded ? '下注成功，请确认' : betting ? '正在下注…' : '确定下注' }}</button>
      </section>
    </div>
    <LiuheChat ref="chat" v-if="chatEnabled && game?.game_id" v-show="tab === 'chat'" :game-id="game.game_id" :active="tab === 'chat'" :profile="profile" :betting-open="bettingOpen" :game-no="state?.game_no" :plays="plays" @unread="unread = $event" @refresh="refresh()" />
  </main>
</template>

<style scoped>
.quick-toolbar{display:flex;align-items:center;justify-content:space-between;padding:0 0 10px}.quick-toolbar strong{font-size:14px}.quick-toolbar button{min-height:34px;padding:0 12px;border:1px solid #ef4141;border-radius:6px;color:#ef4141;background:white;font-size:14px;cursor:pointer}
.liuhe-page{max-width:640px;margin:auto;display:flex;height:100dvh;min-height:0;flex-direction:column;background:#f4f4f4;color:#111;font:12px Arial,"Microsoft YaHei",sans-serif}.liuhe-page :deep(.game-toolbar){flex-shrink:0}.liuhe-tabs{display:flex;min-height:44px;flex-shrink:0;border-bottom:1px solid #ddd;background:#fff}.liuhe-tabs button{flex:1;border:0;background:white;position:relative;font-size:14px}.liuhe-tabs .active{color:#f04141}.liuhe-tabs .active:after{content:'';position:absolute;left:35%;right:35%;bottom:0;height:2px;background:#f04141}.liuhe-tabs b{display:inline-block;margin-left:5px;background:#f04141;color:#fff;border-radius:4px;padding:2px 4px;font-size:11px}.account-strip{display:flex;flex-wrap:wrap;justify-content:space-between;gap:4px;padding:7px 10px;background:#d9f8fc;color:#333}.account-strip b{color:#f04141;font-weight:400}.account-strip em{font-style:normal;color:#777}.liuhe-scroll{min-height:0;flex:1;overflow:auto}.last-area{background:#fff;border-bottom:5px solid #f1f1f1}.last-title{display:flex;align-items:center;justify-content:space-between;padding:9px 12px;border-bottom:1px solid #eee;color:#e8353d}.last-title small{color:#888}.last-title button{border:0;background:none;color:#777}.balls{display:flex;align-items:center;justify-content:center;gap:7px;padding:11px}.ball{display:inline-grid;place-items:center;width:30px;height:30px;border-radius:50%;color:#fff;font-size:14px;font-style:normal;flex-shrink:0}.red{background:#e8353d}.blue{background:#2f7fd6}.green{background:#1f9d55}.plus{color:#999}.draws{background:#fff;border-bottom:5px solid #f1f1f1}.draw{display:flex;align-items:center;justify-content:space-between;padding:6px 10px;border-bottom:1px solid #eee;gap:4px}.draw strong{font-size:12px;font-weight:400}.draw small{display:block;font-size:11px;color:#888}.draw .balls{gap:4px;padding:0}.draw .ball{width:24px;height:24px;font-size:11px}.pages{display:flex;align-items:center;justify-content:center;gap:15px;padding:8px}.pages button{border:1px solid #ddd;border-radius:3px;background:white;padding:5px 8px;color:#555}.pages button:disabled{opacity:.4}.period-bar{display:flex;align-items:center;justify-content:space-between;padding:8px 12px;background:white}.period-bar button{border:0;background:none;color:#f04141;font-size:18px}.types{display:flex;overflow:auto;background:white;border-block:1px solid #ddd}.types button{flex-shrink:0;border:0;border-right:1px solid #eee;background:white;color:#444;min-height:40px;padding:7px 13px}.types .active{background:#f04141;color:#fff}.play-section{padding:12px;background:#389b61}.plays{display:grid;grid-template-columns:repeat(7,minmax(0,1fr));gap:7px}.plays article{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:6px;min-height:62px;border:1px solid #77c697;color:#fff;padding:6px 2px}.plays strong{font-size:13px;font-weight:400}.plays small{color:#ffe561;font-size:11px}.plays .ball{width:26px;height:26px;font-size:12px}.status,.error{margin:0;padding:8px 12px;background:#fff8e1;color:#8a6d1f;font-size:11px;line-height:1.5}.error{background:#ffebeb;color:#b32b2b}
.play-section{background:#fff}
.play-stage{position:relative;display:flex;flex:1;min-height:0;flex-direction:column}
.game-close-state{position:absolute;inset:0;z-index:3;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,.4);color:#fff;font-size:14px;pointer-events:none}
.board-closed .plays article{position:relative}
.board-closed .plays article::after{content:'封盘';position:absolute;inset:0;display:grid;place-items:center;background:rgba(0,0,0,.4);color:#fff;font-size:11px;pointer-events:none}
.period-bar{display:grid;grid-template-columns:minmax(68px,1fr) minmax(0,auto) minmax(68px,1fr);align-items:center;border-bottom:1px solid #ddd;gap:6px}
.period-bar>span:nth-child(2){text-align:center;font-variant-numeric:tabular-nums}
.refresh-control{display:flex;align-items:center;justify-self:end;gap:4px;color:#f04141}
.refresh-control small{font-size:11px}
.refresh-control button{min-width:28px;min-height:28px;padding:0}
.refresh-control button:disabled{opacity:.6}
.refresh-control button span{display:inline-block}
.refresh-control .spinning{animation:liuhe-refresh-spin .8s linear infinite}
@keyframes liuhe-refresh-spin{to{transform:rotate(360deg)}}
@media(prefers-reduced-motion:reduce){.refresh-control .spinning{animation:none}}
.finished-state{color:#18864b;text-align:center;line-height:1.6}
.finished-state small{display:block;color:#777;font-size:11px}
.last-title{display:grid;grid-template-columns:minmax(0,1fr) auto minmax(0,1fr);gap:4px;padding:9px 10px;border-bottom:0}
.last-period{white-space:nowrap}
.pending-ball{background:#e5e5e5;color:#999}
.live-status{text-align:center;margin:0;padding:0 8px 8px;color:#e8353d;font-size:11px}
.revealed{animation:draw-reveal .6s ease-out}
@keyframes draw-reveal{from{box-shadow:0 0 0 5px #ffd568;transform:scale(.85)}to{box-shadow:0 0 0 0 transparent;transform:scale(1)}}
@media(prefers-reduced-motion:reduce){.revealed{animation:none}}
.last-title>.balls{padding:0;gap:5px;color:#777}
.last-title>button{justify-self:end;padding:0;white-space:nowrap}
@media(max-width:440px){.last-title>.balls{gap:3px}.last-title .ball{width:24px;height:24px;font-size:12px}.last-period{font-size:11px}}
@media(max-width:360px){.last-title{padding-inline:6px}.last-title>.balls{gap:2px}.last-title .ball{width:20px;height:20px;font-size:11px}.last-period{font-size:10px}}
.plays article{background:#fff;border-color:#ddd;color:#333}
.plays small{color:#a90000;font-size:12px;font-weight:400}
.last-title .ball{font-size:18px;font-weight:900}
.plays .ball{font-size:14px;font-weight:900}
.plays strong{font-size:14px;font-weight:700;color:#111}
@media(max-width:440px){.last-title .ball{font-size:16px}}
@media(max-width:360px){.last-title .ball{font-size:14px}}
.plays article[aria-disabled="false"]{cursor:pointer}
.plays article.selected{border-color:#f04141;background:#fff0f0;box-shadow:inset 0 0 0 1px #f04141}
.bet-action{display:flex;gap:10px;padding:10px 15px;background:white;border-top:1px solid #eee;flex-shrink:0}
.bet-action button{flex:1;height:40px;border:0;background:#f04141;color:white}.bet-action .reset{flex:0 0 80px;background:#51cdcc}.bet-action button:disabled{background:#ccc}
.bet-feedback{margin:0;padding:10px;background:#fff;color:#333}
.confirm-success-check{color:green;font-size:27px;font-weight:500;line-height:1}
.confirm-submit.success{background:green}
.confirm-row input:disabled,.confirm-quick input:disabled{color:#999}
.bet-success-toast{position:fixed;right:8px;bottom:8px;left:8px;z-index:1100;min-height:48px;display:flex;align-items:center;padding:0 12px;border-radius:2px;background:rgba(30,30,30,.92);color:#fff;font-size:14px;pointer-events:none}
.bet-confirm-mask{position:fixed;inset:0;z-index:1000;display:flex;align-items:center;justify-content:center;padding:14px;background:rgba(0,0,0,.42)}
.bet-confirm-dialog{box-sizing:border-box;width:min(100%,480px);max-height:90dvh;overflow:auto;padding:0 15px 14px;background:#fff;color:#333;font-size:13px}
.bet-confirm-dialog header{display:grid;grid-template-columns:1fr auto 1fr;align-items:center;min-height:40px;border-bottom:1px solid #eee}
.bet-confirm-dialog header b,.confirm-summary b{color:#f04141;font-weight:400}
.bet-confirm-dialog header button{justify-self:end;border:0;background:transparent;color:#ff545b;font-size:30px;line-height:1}
.confirm-quick{display:flex;align-items:center;justify-content:center;gap:8px;height:42px}
.confirm-quick input{box-sizing:border-box;width:88px;height:30px;border:0;background:#f5f5f5;padding:0 8px;text-align:center}
.confirm-head,.confirm-row{display:grid;grid-template-columns:1.25fr .8fr 1fr .8fr .55fr;align-items:center;text-align:center;gap:2px}
.confirm-head{height:34px;border-top:1px solid #eee;border-bottom:1px solid #eee}.confirm-head b{font-weight:600}
.confirm-rows{max-height:50dvh;overflow:auto}.confirm-row{min-height:43px;border-bottom:1px solid #eee;color:#666}
.confirm-row input{box-sizing:border-box;width:100%;max-width:80px;min-width:0;height:30px;justify-self:center;border:0;background:#f5f5f5;text-align:center}
.confirm-row button{border:0;background:transparent;color:#ff545b;font-size:28px;line-height:1}
.confirm-summary{display:flex;justify-content:space-between;gap:4px;flex-wrap:wrap;padding:12px 0;font-size:13px}
.confirm-submit{width:100%;height:41px;border:0;border-radius:2px;background:#f04141;color:#fff;font-size:14px}.confirm-submit:disabled{background:#ccc}
@media(max-width:380px){.bet-confirm-mask{padding:7px}.bet-confirm-dialog{padding-inline:9px}.confirm-head,.confirm-row{grid-template-columns:1.18fr .72fr .95fr .72fr .45fr}}
@media(max-width:360px){.plays{gap:4px}.play-section{padding:8px}.draw{align-items:flex-start;flex-direction:column}.draw .balls{padding-top:5px}}
</style>
